import { Component, effect, inject, NgZone, OnInit, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthService } from './core/auth/auth.service';
import { CspService } from './services/csp-nonce-handler.service';
import { IdleStore } from './store/idle.store';
import { FirebaseManagerService } from './services/firebase.manager.service';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
    private readonly _idleStore = inject(IdleStore);
    private readonly _authService = inject(AuthService);
    private readonly _fuseConfirmationService = inject(FuseConfirmationService);
    private _matDialog = inject(MatDialog);

    /**
     * Constructor
     */
    constructor(
        private _cspService: CspService,
        private _router: Router,
        private _ngZone: NgZone,
        private firebaseManagerService: FirebaseManagerService
    ) {
        effect(() => {
            const status = this._idleStore.status();
            const isAuthenticated = this._authService.authenticated;

            if (status === 'expired' && isAuthenticated) {
                console.log('toink logging out...');
                // clear timers
                this._idleStore.stopTracking();

                // force all active dialogs/modals instantly
                // this clears any open forms, delete boxes and gray backdrops
                // before openning the timeout notifications
                this._matDialog.closeAll();

                this._authService.signOut().subscribe({
                    next: () => console.log('Security: Session revoke autmotically via idle timeout'),
                    error: (err) => console.log('Automatic revocation background error', err),
                });

                const confirmationDialog = this._fuseConfirmationService.open({
                    title: 'Session Timeout',
                    message: 'You have been automatically signed out due to 15 minutes of inactivity.',
                    icon: {
                        show: true,
                        name: 'heroicons_outline:exclamation-triangle',
                        color: 'warning',
                    },
                    actions: {
                        confirm: {
                            show: true,
                            label: 'Sign in again',
                            color: 'primary',
                        },
                        cancel: {
                            show: false,
                        },
                    },
                    dismissible: false,
                });

                confirmationDialog.afterClosed().subscribe(() => {
                    this._router.navigate(['/sign-in'], {
                        queryParams: { reason: 'session-expired' },
                    });
                });
            }
        });

        /**
         * WATCH FOR PAGE REFRESH / INITIAL LOGIN
         * Automatically initializes tracking loops the exact millisecond the user
         * transitions into a verified authenticated state.
         */
        effect(() => {
            const isAuthenticated = this._authService.authenticated;

            if (isAuthenticated) {
                // Wrap in untracked so changes to internal store properties
                // do not accidentally re-trigger this initialization effect.
                untracked(() => {
                    console.log(`Authenticated state detected on load/refresh. Starting idle tracking...`);
                    this._idleStore.startTracking();
                });
            }
        });
    }

    private sessionExpiredListener = () => {
        this._ngZone.run(() => {
            this._router
                .navigate(['/sign-in'], { queryParams: { reason: 'session-expired' } })
                .then((navigationSuccessful) => {
                    if (!navigationSuccessful) {
                        //console.error('ROUTER REJECTED NAVIGATION! A Route Guard (like NoAuthGuard) blocked the login page.');
                    } else {
                        //console.log('Navigation to sign-in successful!');
                    }
                });
        });
    };

    ngOnInit(): void {
        //fetch nonce on component
        this._cspService.fetchNonce().subscribe({
            next: (res) => this._cspService.setNonce(res.nonce),
            error: (err) => console.error('Failed to fetch nonce', err),
        });

        // Listen for the background timeout
        // window.addEventListener('app-session-expired', this.sessionExpiredListener);
        this.firebaseManagerService.startTokenMonitor();
    }
    ngOnDestroy(): void {
        // 3. Clean it up when the app closes
        // window.removeEventListener('app-session-expired', this.sessionExpiredListener);
    }
}
